/**
 * Public "Concierge Digital" presets — one-off shareable destination guides
 * for OTA partners. Each preset reuses the existing Maya / destination-chat
 * AI endpoint; only the public landing copy + system-prompt id live here.
 *
 * Client-safe: no secrets, no server imports.
 */

export type ConciergeSuggestion = { emoji: string; label: string; prompt: string };

export interface ConciergePreset {
  id: string;
  /** UI + AI language (i18n code accepted by /api/destination-chat). */
  lang: "nl" | "es" | "en" | "fr" | "pt" | "it" | "de";
  destination: string;
  /** Tour used for the (soft) recommendation + curated tour context. */
  tourSlug: string;
  tourTitle: string;
  tourUrl: string;
  brand: string;
  kicker: string;
  title: string;
  intro: string;
  inputPlaceholder: string;
  sendLabel: string;
  disclaimer: string;
  errorText: string;
  suggestions: ConciergeSuggestion[];
}

export const CONCIERGE_PRESETS: Record<string, ConciergePreset> = {
  "cozumel-nl": {
    id: "cozumel-nl",
    lang: "nl",
    destination: "Cozumel, Quintana Roo, México",
    tourSlug: "excursion-a-cozumel-desde-cancun-transporte-incluido-1238521",
    tourTitle: "Excursie naar Cozumel met vervoer inbegrepen",
    tourUrl:
      "https://www.mostrandomexico.mx/tour/excursion-a-cozumel-desde-cancun-transporte-incluido-1238521",
    brand: "Welkom bij Mostrando México 🇲🇽",
    kicker: "Digitale reisassistent · 24/7",
    title: "Ontdek Cozumel",
    intro:
      "Ik ben je digitale reisassistent voor Cozumel. Vraag me alles over het eiland, de stranden, het koraalrif, snorkelen, duiken, de Maya-geschiedenis, lokale gastronomie en onze excursies.",
    inputPlaceholder: "Stel je vraag over Cozumel…",
    sendLabel: "Versturen",
    disclaimer:
      "Informatie ter oriëntatie. Prijzen, tijden en inclusies staan altijd op de pagina van de excursie.",
    errorText: "De assistent is even niet beschikbaar. Probeer het opnieuw.",
    suggestions: [
      { emoji: "🌊", label: "Wat moet ik zien in Cozumel?", prompt: "Ik ga voor het eerst naar Cozumel. Wat mag ik absoluut niet missen?" },
      { emoji: "🐠", label: "Alles over snorkelen", prompt: "Vertel me alles over snorkelen in Cozumel: riffen, zeeleven en tips." },
      { emoji: "🤿", label: "Waarom is Cozumel zo bekend om duiken?", prompt: "Waarom is Cozumel wereldwijd zo bekend om duiken?" },
      { emoji: "🏝️", label: "De mooiste stranden", prompt: "Welke stranden van Cozumel zijn het mooist en wat is het verschil tussen de oost- en westkust?" },
      { emoji: "🌮", label: "Wat moet ik eten?", prompt: "Wat moet ik eten in Cozumel? Typische gerechten en drankjes van Quintana Roo." },
      { emoji: "🏛️", label: "Maya-geschiedenis", prompt: "Vertel me over de Maya-geschiedenis van Cozumel en San Gervasio." },
      { emoji: "🚢", label: "Hoe werkt de ferry?", prompt: "Hoe werkt de ferry naar Cozumel? Waar vertrekt hij en hoe lang duurt de oversteek?" },
      { emoji: "🚐", label: "Excursie naar Cozumel met vervoer", prompt: "Leg me jullie excursie naar Cozumel met vervoer inbegrepen uit: wat is inbegrepen, duur en wat moet ik meenemen?" },
    ],
  },
};

export function getConciergePreset(id: string | undefined): ConciergePreset | null {
  if (!id) return null;
  return CONCIERGE_PRESETS[id.toLowerCase()] ?? null;
}
